import { ref, computed } from "vue";
import { useBlocks } from "./useBlocks.js";
import type {
  User,
  Guest,
  RegisterRequest,
  RegisterResponse,
  LoginRequest,
  LoginResponse,
  MeResponse,
  ErrorResponse,
  ValidationError,
  ForgotPasswordResponse,
} from "../../shared/api.js";
import { ApiError, apiPost } from "../utils/api.js";
import { apiUrl } from "../utils/serverBase.js";

const user = ref<User | null>(null);
const guest = ref<Guest | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);
const validationErrors = ref<ValidationError[]>([]);

export function useAuth() {
  const isAuthenticated = computed(() => user.value !== null);
  const isGuest = computed(() => !user.value && guest.value !== null);
  const emailVerified = computed(() => user.value?.emailVerified ?? false);

  async function fetchCurrentUser(): Promise<void> {
    try {
      const response = await fetch(apiUrl("/api/auth/me"), {
        credentials: "include", // Important: send cookies
        cache: "no-store",
      });

      if (response.ok) {
        const data: MeResponse = await response.json();
        user.value = data.user;
        guest.value = data.guest;
      }
    } catch (err) {
      console.error("Failed to fetch current user:", err);
    }
  }

  async function register(
    email: string,
    password: string,
    displayName: string,
    subscribeNewsletter = false,
  ): Promise<void> {
    loading.value = true;
    error.value = null;
    validationErrors.value = [];

    try {
      const body: RegisterRequest = { email, password, displayName, subscribeNewsletter };
      const response = await fetch(apiUrl("/api/auth/register"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // Important: send/receive cookies
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data: RegisterResponse = await response.json();
        user.value = data.user;
        guest.value = data.guest;
      } else {
        const data: ErrorResponse = await response.json();
        if (data.errors) {
          validationErrors.value = data.errors;
        } else {
          error.value = data.error || "Registration failed";
        }
        throw new Error(data.error || "Registration failed");
      }
    } finally {
      loading.value = false;
    }
  }

  async function login(email: string, password: string): Promise<void> {
    loading.value = true;
    error.value = null;
    validationErrors.value = [];

    try {
      const body: LoginRequest = { email, password };
      let response: Response;
      try {
        response = await fetch(apiUrl("/api/auth/login"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include", // Important: send/receive cookies
          body: JSON.stringify(body),
        });
      } catch {
        error.value = "Could not reach the server. Please try again.";
        throw new Error(error.value);
      }

      if (response.ok) {
        const data: LoginResponse = await response.json();
        user.value = data.user;
        // Fetch full guest info
        await fetchCurrentUser();
      } else {
        const data: ErrorResponse = await response.json();
        if (data.errors) {
          validationErrors.value = data.errors;
        } else {
          error.value = data.error || "Login failed";
        }
        throw new Error(data.error || "Login failed");
      }
    } finally {
      loading.value = false;
    }
  }

  async function logout(): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      try {
        await apiPost("/api/auth/logout");
      } catch (err) {
        if (err instanceof ApiError) {
          error.value = "Logout failed. Please try again.";
        } else {
          error.value = "Could not reach the server. Please try again.";
        }
        return;
      }

      user.value = null;
      // The block list belongs to the account that just signed out.
      useBlocks().clearBlocks();
      // Guest info remains, allowing continued play as guest
      // Or fetch new guest session
      await fetchCurrentUser();
    } finally {
      loading.value = false;
    }
  }

  async function requestPasswordReset(email: string): Promise<string> {
    loading.value = true;
    error.value = null;
    validationErrors.value = [];

    try {
      const response = await fetch(apiUrl("/api/auth/forgot-password"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => ({})) as
        Partial<ForgotPasswordResponse & ErrorResponse>;
      if (!response.ok) {
        if (data.errors) validationErrors.value = data.errors;
        else error.value = data.error || "Password reset request failed";
        throw new Error(data.error || "Password reset request failed");
      }
      return data.message || "If an account exists for that email, a password reset link has been sent.";
    } catch (err) {
      if (!(err instanceof Error) || !err.message.includes("Password reset request failed")) {
        if (!error.value && validationErrors.value.length === 0) {
          error.value = "Could not reach the server. Please try again.";
        }
      }
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function verifyEmail(token: string): Promise<void> {
    const response = await fetch(apiUrl("/api/auth/verify-email"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ token }),
    });
    const data = await response.json().catch(() => ({})) as { user?: User; error?: string };
    if (!response.ok) {
      throw new Error(data.error || "This verification link is invalid or has expired");
    }
    if (data.user) user.value = data.user;
  }

  async function resendVerification(): Promise<string> {
    const response = await fetch(apiUrl("/api/auth/resend-verification"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    const data = await response.json().catch(() => ({})) as { message?: string; error?: string };
    if (!response.ok) {
      throw new Error(data.error || "Could not send the verification email. Please try again.");
    }
    return data.message || "A new verification link has been sent.";
  }

  function clearError(): void {
    error.value = null;
    validationErrors.value = [];
  }

  return {
    user,
    guest,
    loading,
    error,
    validationErrors,
    isAuthenticated,
    isGuest,
    emailVerified,
    register,
    login,
    logout,
    requestPasswordReset,
    verifyEmail,
    resendVerification,
    fetchCurrentUser,
    clearError,
  };
}
